import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

// Native ERP port of the standalone Sales Reporter app's "Plans" tab.
// Mirrors the actual api/add_plan.php: purpose is the required field
// (contact_name is optional — PHP stores it as NULL if left blank), and
// plan_date has no today-fallback (blank stays unset; a malformed value
// is rejected rather than silently reset, unlike visit_date/expense_date
// in Phase 2). Same self-service scope (a rep only ever sees/edits/
// deletes plans they created themselves) and the same three buckets
// (Next Day / Next Week / Pending Work) with an open/done status.
//
// Note on parity: update_plan.php / delete_plan.php / get_plans.php
// themselves weren't available when this was built (see
// backend/sales_report.py's Phase 3 note) — marking a plan "done" via
// the edit sheet's checkbox is an assumption about how update_plan.php
// behaves, flagged for a follow-up parity check.
const PLAN_TYPES = [
  { value: "next_day", label: "Next Day" },
  { value: "next_week", label: "Next Week" },
  { value: "pending_work", label: "Pending Work" },
];

const empty = {
  plan_type: "next_day",
  contact_name: "",
  purpose: "",
  plan_date: "",
  notes: "",
  status: "open",
};

export default function SalesPlansPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => setList((await api.get("/sales-report/plans")).data);
  useEffect(() => { load(); }, []);

  const startCreate = (planType) => { setEditing(null); setForm({ ...empty, plan_type: planType || "next_day" }); setOpen(true); };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...empty, ...p, plan_date: p.plan_date || "" }); setOpen(true); };

  const save = async () => {
    if (busy) return;
    if (!form.purpose.trim()) return toast.error("Please enter the purpose/task");
    setBusy(true);
    try {
      if (editing) await api.put(`/sales-report/plans/${editing}`, form);
      else await api.post("/sales-report/plans", form);
      toast.success(editing ? "Plan updated" : "Plan added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (p) => {
    try {
      await api.put(`/sales-report/plans/${p.id}`, { ...p, status: p.status === "done" ? "open" : "done" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete this plan — "${p.purpose}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/sales-report/plans/${p.id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const buckets = useMemo(() => ({
    next_day: list.filter((p) => p.plan_type === "next_day"),
    next_week: list.filter((p) => p.plan_type === "next_week"),
    pending_work: list.filter((p) => p.plan_type === "pending_work"),
  }), [list]);

  return (
    <div className="space-y-6" data-testid="sales-plans-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales Report</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Plans
          </h1>
          <p className="text-sm text-slate-500 mt-1">Next day, next week & pending work — mark done as you go.</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => startCreate()} data-testid="new-plan-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Plan
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader><SheetTitle data-testid="plan-sheet-title">{editing ? "Edit Plan" : "New Plan"}</SheetTitle></SheetHeader>

            <div className="space-y-5 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Bucket</Label>
                  <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                    <SelectTrigger data-testid="plan-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLAN_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Plan Date</Label>
                  <Input type="date" value={form.plan_date} onChange={(e) => setForm({ ...form, plan_date: e.target.value })} data-testid="plan-date-input" />
                </div>
              </div>

              <div>
                <Label>Purpose / Task *</Label>
                <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="plan-purpose-input" />
              </div>
              <div>
                <Label>Contact Name</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="plan-contact-input" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="plan-notes-input" />
              </div>

              {editing && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={form.status === "done"} onCheckedChange={(v) => setForm({ ...form, status: v ? "done" : "open" })} data-testid="plan-status-checkbox" />
                  Done
                </label>
              )}
            </div>

            <SheetFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={busy} data-testid="save-plan-btn">Save</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {PLAN_TYPES.map((bucket) => (
          <div key={bucket.value} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`plan-bucket-${bucket.value}`}>
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold">{bucket.label}</span>
              <Button variant="ghost" size="icon" onClick={() => startCreate(bucket.value)} data-testid={`add-to-${bucket.value}`}>
                <Plus className="w-4 h-4 text-slate-500" />
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {buckets[bucket.value].map((p) => (
                <div key={p.id} className="p-3 flex items-start gap-3" data-testid={`plan-row-${p.id}`}>
                  <Checkbox
                    className="mt-1"
                    checked={p.status === "done"}
                    onCheckedChange={() => toggleStatus(p)}
                    data-testid={`plan-toggle-${p.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${p.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>
                      {p.purpose}
                    </div>
                    {p.contact_name && <div className="text-xs text-slate-500">{p.contact_name}</div>}
                    {p.plan_date && <div className="text-xs text-slate-400 mt-0.5">{p.plan_date}</div>}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(p)} data-testid={`edit-plan-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`delete-plan-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
              {buckets[bucket.value].length === 0 && (
                <div className="p-6 text-center text-slate-400 text-sm">Nothing here yet.</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
